import React, { useState } from 'react';
import './CreateAssessmentModal.css';
import { 
    X, 
    Code, 
    Database, 
    Network, 
    Sparkles, 
    ChevronLeft,
    Check
} from 'lucide-react';
import { useAuthContext } from '../Auth/AuthContext';
import { CustomSelect } from '../CustomStyling';
import config from '../../config';
import RingLoader from "react-spinners/RingLoader";


const CreateAssessmentModal = ({ isOpen, onClose, refreshPaths }) => {
    const { userObjId } = useAuthContext();
    const [step, setStep] = useState(1); // 1: Choose Interest, 2: Programming Options
    const [selectedInterest, setSelectedInterest] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const websocketUrl = "wss://ycbrz3lmnc.execute-api.us-west-2.amazonaws.com/production/";

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const interests = [
        { id: 'programming', name: 'Programming', icon: Code, color: '#BE05FA', enabled: true },
        { id: 'data_science', name: 'Data Science', icon: Database, color: '#BE05FA', enabled: false },
        { id: 'computer_networks', name: 'Computer Networks', icon: Network, color: '#BE05FA', enabled: false },
        { id: 'custom', name: 'Custom', icon: Sparkles, color: '#BE05FA', enabled: false },
    ];

    const programmingLanguages = [
        { id: 'c', name: 'C' },
        { id: 'cpp', name: 'C++' },
        { id: 'python', name: 'Python' },
        { id: 'java', name: 'Java' },
        { id: 'javascript', name: 'JavaScript' },
    ];

    const databases = [
        { id: 'sql', name: 'SQL' },
        { id: 'nosql', name: 'NoSQL' },
    ];

    const handleInterestClick = (interest) => {
        if (!interest.enabled) return;
        setSelectedInterest(interest.id);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
        setSelectedLanguage(null);
        setSelectedDatabase(null);
        setError('');
    };

    const handleClose = () => {
        setStep(1);
        setSelectedInterest(null);
        setSelectedLanguage(null);
        setSelectedDatabase(null);
        setError('');
        setIsLoading(false);
        onClose();
    };

    const sendWebSocketMessageWithRetry = (websocketInstance, message, retries = 3, interval = 1000) => {
        let attempts = 0;

        const sendMessage = () => {
            if (websocketInstance.readyState === WebSocket.OPEN) {
                websocketInstance.send(JSON.stringify(message));
            } else if (attempts < retries) {
                attempts++;
                setTimeout(sendMessage, interval);
            } else {
                console.error("Failed to send WebSocket message after multiple attempts.");
                setError('Connection failed. Please try again.');
                setIsLoading(false);
            }
        };
        sendMessage();
    };

    const handleWebSocketMessage = async (event) => {
        if (!event.data || event.data.trim() === "") {
            console.warn("Empty WebSocket message received.");
            return;
        }
    
        try {
            const data = JSON.parse(event.data);
            console.log("WebSocket message received:", data);
    
            if (data.connectionid) {
                // Step 1: Connection ID received, now send payload to API
                const payload = {
                    user_obj_id: userObjId,
                    session_type: selectedInterest,
                    preferred_language: selectedLanguage,
                    preferred_database: selectedDatabase,
                    connection_id: data.connectionid,
                };
    
                console.log("Sending payload to API:", payload);
    
                try {
                    const response = await fetch(`${config.restAPI}/assessments/createAssessment/`, {
                        method: "POST",
                        credentials: 'include',
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    });
    
                    const responseData = await response.json();
                    console.log("Session created:", responseData);
    
                    if (!response.ok) {
                        throw new Error(responseData.error || 'Failed to create assessment');
                    }
                    // Don't close here - wait for session_data from WebSocket
    
                } catch (apiError) {
                    console.error("API Error:", apiError);
                    setError(apiError.message || 'Failed to create assessment.');
                    setIsLoading(false);
                }
    
            } else if (data.session_data) {
                // Step 2: Session data received from WebSocket - SUCCESS!
                console.log("Session data received:", data.session_data);
                refreshPaths();
                setIsLoading(false);
                handleClose();
    
            } else if (data.error) {
                // Error received from WebSocket
                console.error("Error from server:", data.error);
                setError(data.error || 'Failed to create session.');
                setIsLoading(false);
            }
    
        } catch (error) {
            console.error("Error parsing WebSocket message:", error);
            setError('Something went wrong. Please try again.');
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!selectedLanguage) {
            setError('Please select a programming language');
            return;
        }
        if (!selectedDatabase) {
            setError('Please select a database');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const websocketInstance = new WebSocket(websocketUrl);

            websocketInstance.onopen = () => {
                const message = { action: "SendMessage", message: "get data" };
                sendWebSocketMessageWithRetry(websocketInstance, message);
            };

            websocketInstance.onmessage = (event) => handleWebSocketMessage(event);

            websocketInstance.onerror = (error) => {
                console.error("WebSocket error:", error);
                setError('Connection failed. Please try again.');
                setIsLoading(false);
            };

            websocketInstance.onclose = () => {
                console.log("WebSocket connection closed.");
            };

        } catch (error) {
            console.error("Error creating session:", error);
            setError('Something went wrong. Please try again.');
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    if (isLoading) {
        return (
            <div className="assessment-modal-overlay">
                <div className="assessment-modal assessment-loading-modal">
                    <div className="loading-modal-content">
                        <RingLoader color="#BE05FA" size={80} />
                        <h2 className="loading-title">Creating Your Assessment</h2>
                        <p className="loading-subtitle">Please wait while we set things up for you</p>
                        <div className="loading-info-box">
                            <Sparkles size={14} />
                            <span>Your assessment is being tailored based on your selected preferences for <strong>{selectedLanguage}</strong> and <strong>{selectedDatabase}</strong></span>
                        </div>
                        {/* <button className="loading-cancel-btn" onClick={handleClose}>Cancel</button> */}
                    </div>
                </div>
            </div>
        );
    }
    

    return (
        <div className="assessment-modal-overlay" onClick={handleClose}>
            <div className="assessment-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="assessment-modal-header">
                    {step === 2 && (
                        <button className="back-btn" onClick={handleBack}>
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div className="assessment-header-content">
                        <h2>{step === 1 ? 'Choose your Interest' : 'Programming Options'}</h2>
                        <p>{step === 1 ? 'Select from below options' : 'Select your preferred language and database'}</p>
                    </div>
                    <button className="close-btn" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="assessment-modal-body">
                    {step === 1 && (
                        <div className="interest-grid">
                            {interests.map((interest) => {
                                const IconComponent = interest.icon;
                                return (
                                    <div
                                        key={interest.id}
                                        className={`interest-card ${!interest.enabled ? 'disabled' : ''} ${selectedInterest === interest.id ? 'selected' : ''}`}
                                        onClick={() => handleInterestClick(interest)}
                                    >
                                        <div 
                                            className="interest-icon" 
                                            style={{ backgroundColor: interest.color }}
                                        >
                                            <IconComponent size={24} color="white" />
                                        </div>
                                        <span className="interest-name">{interest.name}</span>
                                        {!interest.enabled && (
                                            <span className="coming-soon">Coming Soon</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="programming-options">
                            {/* Language Selection */}
                            <div className="option-section">
                                {/* <h3>Programming Language</h3> */}
                                <CustomSelect
                                    label="Programming Language"
                                    value={selectedLanguage || ''}
                                    onChange={(value) => setSelectedLanguage(value)}
                                    options={programmingLanguages.map((lang) => ({
                                        label: lang.name,
                                        value: lang.id
                                    }))}
                                    placeholder="Select a language"
                                >
                                </CustomSelect>
                            </div>

                            {/* Database Selection */}
                            <div className="option-section">
                                {/* <h3>Database</h3> */}
                                        <CustomSelect
                                    label="Database"
                                    value={selectedDatabase || ''}
                                    onChange={(value) => setSelectedDatabase(value)}
                                    options={databases.map((db) => ({
                                        label: db.name,
                                        value: db.id
                                    }))}
                                    placeholder="Select a database"
                                >
                                </CustomSelect>
                            </div>

                            {error && <p className="error-message">{error}</p>}

                            {/* Create Button */}
                            <button 
                                className="create-assessment-btn"
                                onClick={handleCreate}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Assessment'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateAssessmentModal;